/**
 * Who a costing screen, query or pending request belongs to (batch 8 T11, #3032).
 *
 * Cost figures and the keyed commands that change them belong to ONE signed-in session of one user in one
 * tenant, with the permissions that session holds right now. This file turns the existing `authStore`
 * contracts into that, once:
 *
 *  - `scope` = `tenantId` + `userId` + `sessionFamilyId`. It is what is persisted next to a pending
 *    request. It survives a reload and a token rotation (the family is stable across both, and the
 *    tokens are never part of it) and it does not survive a new login, a logout or another tenant or
 *    user (a new login gets a new family). A session that has no family yet (restored from before
 *    families existed) is the one legacy session of that user: a new login always creates a family, so
 *    it cannot be confused with it.
 *  - `sessionEpoch` is the in-memory generation. It is NOT part of the persisted scope (a reload resets
 *    it, and a reload of the same session is legitimate); it only fences work that is in flight in this
 *    page: a request started under one epoch cannot report into another.
 *  - permissions are read as primitives, so a change in the user's permissions re-renders the screen in
 *    the same pass, and are re-read from the store at the moment of every dispatch and every late result.
 */
import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';

export const COSTING_CONFIRM_PERMISSION = 'inventory.valuation.confirm';
export const COSTING_READ_PERMISSION = 'inventory.read';
/** The server shows cost values and hints to a financial reader or to someone who may confirm. */
export const COSTING_FINANCIAL_PERMISSION = 'reports.view_financial';

export interface CostingScope {
  readonly tenantId: string;
  readonly userId: string;
  readonly sessionFamilyId: string;
}

export interface CostingAccess {
  /** Identity of the signed-in session, also while the portal is locked. `null`: nobody is signed in. */
  readonly scope: CostingScope | null;
  /** Signed in and not locked: the only state in which anything may be read or sent. */
  readonly active: boolean;
  readonly canRead: boolean;
  readonly canConfirm: boolean;
  /** May see cost values and hints (what the server calls `valuesVisible`). */
  readonly canSeeValues: boolean;
}

const LEGACY_FAMILY = 'legacy-session';

export const scopeEquals = (a: CostingScope | null, b: CostingScope | null): boolean =>
  a !== null && b !== null && a.tenantId === b.tenantId && a.userId === b.userId && a.sessionFamilyId === b.sessionFamilyId;

/** A string that is equal exactly for equal scopes. */
export const scopeId = (scope: CostingScope): string => JSON.stringify([scope.tenantId, scope.userId, scope.sessionFamilyId]);

type AuthSnapshot = ReturnType<typeof useAuthStore.getState>;

function scopeOf(state: Pick<AuthSnapshot, 'user' | 'isAuthenticated' | 'sessionFamilyId'>): CostingScope | null {
  const user = state.user;
  if (!state.isAuthenticated || user === null) return null;
  if (typeof user.id !== 'string' || user.id === '' || typeof user.tenantId !== 'string' || user.tenantId === '') return null;
  return { tenantId: user.tenantId, userId: user.id, sessionFamilyId: state.sessionFamilyId ?? LEGACY_FAMILY };
}

/** The access of the store as it is at this moment. Use it at the moment of an action, never a captured copy. */
export function readCostingAccess(state: AuthSnapshot = useAuthStore.getState()): CostingAccess {
  const scope = scopeOf(state);
  const active = scope !== null && !state.isLocked;
  const canConfirm = active && state.hasPermission(COSTING_CONFIRM_PERMISSION);
  return {
    scope,
    active,
    canConfirm,
    canRead: canConfirm || (active && state.hasPermission(COSTING_READ_PERMISSION)),
    canSeeValues: canConfirm || (active && state.hasPermission(COSTING_FINANCIAL_PERMISSION)),
  };
}

/**
 * The reactive access. Every input is a primitive (never the stable `hasPermission` function, whose
 * identity does not change when the user's permissions do), so a downgrade re-renders synchronously.
 */
export function useCostingAccess(): CostingAccess {
  const tenantId = useAuthStore((state) => state.user?.tenantId ?? null);
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const family = useAuthStore((state) => state.sessionFamilyId);
  const authenticated = useAuthStore((state) => state.isAuthenticated);
  const locked = useAuthStore((state) => state.isLocked);
  const holdsConfirm = useAuthStore((state) => state.hasPermission(COSTING_CONFIRM_PERMISSION));
  const holdsRead = useAuthStore((state) => state.hasPermission(COSTING_READ_PERMISSION));
  const holdsFinancial = useAuthStore((state) => state.hasPermission(COSTING_FINANCIAL_PERMISSION));
  return useMemo(() => {
    const scope =
      authenticated && typeof tenantId === 'string' && tenantId !== '' && typeof userId === 'string' && userId !== ''
        ? { tenantId, userId, sessionFamilyId: family ?? LEGACY_FAMILY }
        : null;
    const active = scope !== null && !locked;
    return {
      scope,
      active,
      canConfirm: active && holdsConfirm,
      canRead: active && (holdsConfirm || holdsRead),
      canSeeValues: active && (holdsConfirm || holdsFinancial),
    };
  }, [tenantId, userId, family, authenticated, locked, holdsConfirm, holdsRead, holdsFinancial]);
}

/**
 * Identity of everything one signed-in owner may see: scope plus the financial and confirm rights. A
 * downgrade is another owner, so nothing cached for the richer one is ever read by the poorer one.
 */
export function ownerKey(access: CostingAccess): string {
  if (access.scope === null || !access.active) return 'none';
  return JSON.stringify([access.scope.tenantId, access.scope.userId, access.scope.sessionFamilyId, access.canRead, access.canConfirm, access.canSeeValues]);
}

/** A write was refused before it left the page: the session, tenant or permission changed since it began. */
export class OwnerChangedError extends Error {
  constructor() {
    super('The session or its permissions changed before the request was sent');
    this.name = 'OwnerChangedError';
  }
}

export const isOwnerChanged = (error: unknown): error is OwnerChangedError => error instanceof OwnerChangedError;

/** A fence: what an in-flight action started under, checked again before it may change anything. */
export interface Fence {
  readonly scope: CostingScope;
  readonly epoch: number;
  readonly permission: string;
}

/** Takes the fence of NOW for `scope`, or `null` when `scope` is not the active session holding `permission`. */
export function takeFence(scope: CostingScope, permission: string = COSTING_CONFIRM_PERMISSION): Fence | null {
  const state = useAuthStore.getState();
  const access = readCostingAccess(state);
  if (!access.active || !scopeEquals(access.scope, scope) || !state.hasPermission(permission)) return null;
  return { scope, epoch: state.sessionEpoch, permission };
}

/** Whether what a fence was taken under still holds: same session, same generation, permission still held. */
export function fenceHolds(fence: Fence): boolean {
  const state = useAuthStore.getState();
  const access = readCostingAccess(state);
  return access.active && scopeEquals(access.scope, fence.scope) && state.sessionEpoch === fence.epoch && state.hasPermission(fence.permission);
}

/**
 * Drops every costing query of another owner as soon as the owner changes, so a cached cost response of a
 * previous session, tenant or permission set is not merely unreachable but gone.
 */
export function useCostingCacheGuard(currentOwnerKey: string, allKey: readonly unknown[]): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.removeQueries({
      queryKey: allKey,
      predicate: (query) => query.queryKey[allKey.length] !== currentOwnerKey,
    });
  }, [queryClient, currentOwnerKey, allKey]);
}
